import React from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Banner,
  Button,
  Page,
  PageHeader,
  PillToggle,
  StatusBadge,
  normalizeRunStatus,
  EmptyState,
  LoadingOrbit,
  TagBadge,
  Text,
  Flex,
  useToast,
  type PillToggleOption,
  type StatusIconBarItem,
} from '@chatsdet/ui';
import { ArrowClockwise, Play, Rows, SquaresFour, Stop } from '@phosphor-icons/react';
import { useParams } from '@tanstack/react-router';
import { AgentPromptButton } from '../components/agent-prompts/AgentPromptButton';
import { API_URL } from '../../config';
import { VisualChangesBadge } from './visual/VisualChangesBadge';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { RunTriageLockup } from '../../features/triage/RunTriageLockup';
import { useExternalRun } from '../../features/external-tests/useExternalRun';
import { useExternalRunProgress } from '../../features/external-tests/useExternalRunProgress';
import {
  describeExternalCancel,
  useCancelExternalRun,
  useRerunExternalRun,
  useSetAreaDisabled,
} from '../../features/external-tests/useExternalRunActions';
import type {
  ExternalRunOverviewDto,
  ExternalScenarioOverviewDto,
  RunAreaDto,
} from '../../features/external-tests/useExternalRun';
import { emptyResultsState } from '../../features/external-tests/emptyResultsState';
import {
  areaSummaryMessage,
  areaToSummaryStatus,
  buildAreaStateOptions,
  filterAreas,
} from '../../features/external-tests/areaSummary';
import { ExternalTestsTable } from './ExternalRunDetail/ExternalTestsTable';
import { ExternalRunProgress } from './ExternalRunDetail/ExternalRunProgress';
import { ExternalRunAreas } from './ExternalRunDetail/ExternalRunAreas';
import { ExternalAreasFilterBar } from './ExternalRunDetail/ExternalAreasFilterBar';
import type { ExternalRunDetailSearchParams } from '../../router';

type ResultsView = 'grouped' | 'flat';

const RESULTS_VIEW_OPTIONS: readonly PillToggleOption<ResultsView>[] = [
  { value: 'grouped', label: 'Grouped', icon: <SquaresFour size={14} /> },
  { value: 'flat', label: 'Flat', icon: <Rows size={14} /> },
];

/**
 * A scenario's verdict in the vocabulary the shared run summary speaks. The
 * summary is the same component the native suite runs use, so an external run
 * reads identically to one of ours — that is the whole point of surfacing them
 * in the same table.
 */
function toSummaryStatus(
  verdict: ExternalScenarioOverviewDto['verdict']
): StatusIconBarItem['status'] {
  switch (verdict) {
    case 'passed':
      return 'success';
    case 'flaky':
      return 'flaky_success';
    case 'failed':
      return 'failed';
    default:
      return 'skipped';
  }
}

/**
 * The header status badge's label, exhaustive over `run.status` so a new
 * status is a compile error here rather than silently falling through to
 * the raw (lowercase, unstyled) status string.
 */
function headerStatusLabel(
  status: ExternalRunOverviewDto['status'],
  counts: { passed: number; failed: number; skipped: number }
): string {
  switch (status) {
    case 'sealed':
      return `${counts.passed} passed · ${counts.failed} failed${counts.skipped > 0 ? ` · ${counts.skipped} skipped` : ''}`;
    case 'open':
    case 'collecting':
      return 'Running';
    case 'canceled':
      return 'Canceled';
  }
}

export function ExternalRunDetail() {
  const { runId } = useParams({ from: '/runs/external/$runId' });
  const navigate = useNavigate();
  const search = useSearch({ from: '/runs/external/$runId' });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // `areaQ`/`areaStates` filter the areas grid client-side only — pulled out
  // by name below so `useExternalRun`'s fetch never sees them. Flat view
  // shows the whole run — an `area` left over from a prior drill-in (e.g.
  // toggling view without clearing it, or a hand-edited URL) must not narrow
  // this fetch, or "flat" would silently render one area's rows.
  const queryParams = {
    page: search.page,
    pageSize: search.pageSize,
    q: search.q,
    verdicts: search.verdicts,
    area: search.view === 'flat' ? undefined : search.area,
  };
  const { data: run, isLoading, isFetching, isError } = useExternalRun(runId, queryParams);
  const cancelRun = useCancelExternalRun();
  const rerunRun = useRerunExternalRun();
  const setAreaDisabled = useSetAreaDisabled();

  // The stream nudges the poll the moment a shard reports, instead of waiting
  // out the remaining 3s. It never carries the results themselves — the poll is
  // the authority for those, and stays the backstop when SSE drops.
  const refetchItems = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['external-run', runId] });
  }, [queryClient, runId]);
  const progressRunId = run?.status === 'sealed' || run?.status === 'canceled' ? null : runId;
  const progressState = useExternalRunProgress(progressRunId, run?.progress ?? null, refetchItems);

  // One summary entry per TEST (the ticketed scenario, e.g. WEB-31415) — not per
  // step. A 1454-step suite is ~34 tests; charting the steps drowns the signal in
  // per-action noise and disagrees with how the customer's own tickets are filed.
  const summaryItems = React.useMemo<StatusIconBarItem[]>(
    () =>
      (run?.summary.items ?? []).map((scenario) => ({
        id: scenario.routeItemId,
        name: scenario.label,
        status: toSummaryStatus(scenario.verdict),
        durationMs: scenario.durationMs,
        errorMessage: scenario.firstFailureMessage,
      })),
    [run]
  );

  // One entry per AREA rather than per test — used only when the areas grid
  // is what's showing (a multi-area run with no area selected), since
  // `summary.items` is empty for that scope (see `useExternalRun`'s docstring).
  const areaSummaryItems = React.useMemo<StatusIconBarItem[]>(
    () =>
      (run?.areas ?? []).map((area) => ({
        id: String(area.index),
        name: area.label,
        status: areaToSummaryStatus(area),
        durationMs: area.durationMs,
        message: areaSummaryMessage(area),
      })),
    [run]
  );

  // Client-side filters for the areas grid only — `areaQ`/`areaStates` never
  // reach the API (see `queryParams` above). Hoisted above the early returns
  // (and memoized) so a still-loading/error render doesn't skip these hooks —
  // React requires hooks to run in the same order every render — and so an
  // unrelated re-render (an SSE tick, a keystroke elsewhere) doesn't hand
  // `ExternalRunAreas` a new `areas` array identity and defeat its own
  // `rows` memoization.
  const filteredAreas = React.useMemo(
    () =>
      filterAreas(run?.areas ?? [], {
        q: search.areaQ ?? '',
        states: search.areaStates ?? [],
      }),
    [run?.areas, search.areaQ, search.areaStates]
  );
  const areaStateOptions = React.useMemo(
    () => buildAreaStateOptions(run?.areas ?? []),
    [run?.areas]
  );
  // A disabled area was never launched and never will be by this run — it
  // must not count toward the denominator either, or a run with any disabled
  // areas could never reach 100% complete.
  const nonDisabledAreas = React.useMemo(
    () => (run?.areas ?? []).filter((area) => area.state !== 'disabled'),
    [run?.areas]
  );
  // An area only stops changing once it's `completed`, `failed`, or
  // `canceled` — used to count AREAS (not tests) complete for the
  // areas-overview lockup below, so "N/M areas complete" agrees with what
  // the grid itself shows instead of reading full the moment the first
  // shard reports a result, and reaches 100% for a canceled run instead of
  // stalling short.
  const completedAreaCount = React.useMemo(
    () =>
      nonDisabledAreas.filter(
        (area) => area.state === 'completed' || area.state === 'failed' || area.state === 'canceled'
      ).length,
    [nonDisabledAreas]
  );

  const openTest = React.useCallback(
    (itemId: string) => {
      navigate({ to: '/runs/external/$runId/item/$itemId', params: { runId, itemId } });
    },
    [navigate, runId]
  );

  const changePage = React.useCallback(
    (page: number) => {
      navigate({
        to: '/runs/external/$runId',
        params: { runId },
        search: (previous: ExternalRunDetailSearchParams) => ({ ...previous, page }),
      });
    },
    [navigate, runId]
  );

  const changePageSize = React.useCallback(
    (pageSize: number) => {
      navigate({
        to: '/runs/external/$runId',
        params: { runId },
        search: (previous: ExternalRunDetailSearchParams) => ({
          ...previous,
          page: 1,
          pageSize,
        }),
      });
    },
    [navigate, runId]
  );

  const updateFilters = React.useCallback(
    (updates: Partial<ExternalRunDetailSearchParams>) => {
      navigate({
        to: '/runs/external/$runId',
        params: { runId },
        search: (previous: ExternalRunDetailSearchParams) => {
          const merged: ExternalRunDetailSearchParams = { ...previous, ...updates };
          if (!merged.q) delete merged.q;
          if (!merged.verdicts || merged.verdicts.length === 0) delete merged.verdicts;
          return merged;
        },
      });
    },
    [navigate, runId]
  );

  const selectArea = React.useCallback(
    (areaIndex: number) => {
      navigate({
        to: '/runs/external/$runId',
        params: { runId },
        search: (previous: ExternalRunDetailSearchParams) => ({
          ...previous,
          // Explicit, not inherited from `previous.view`: the lockup's area
          // icons show in flat view too (see `isAreasView`), so a click there
          // must switch into grouped — the only view whose fetch actually
          // scopes to `area` (see `queryParams` above) — or the URL would
          // carry `area` while still rendering the whole run's flat table.
          view: 'grouped',
          area: areaIndex,
          page: 1,
        }),
      });
    },
    [navigate, runId]
  );

  const clearAreaSelection = React.useCallback(() => {
    navigate({
      to: '/runs/external/$runId',
      params: { runId },
      search: (previous: ExternalRunDetailSearchParams) => {
        const next = { ...previous, page: 1 };
        delete next.area;
        return next;
      },
    });
  }, [navigate, runId]);

  const changeView = React.useCallback(
    (view: ResultsView) => {
      navigate({
        to: '/runs/external/$runId',
        params: { runId },
        search: (previous: ExternalRunDetailSearchParams) => {
          const next = { ...previous, view, page: 1 };
          delete next.area;
          delete next.areaQ;
          delete next.areaStates;
          return next;
        },
      });
    },
    [navigate, runId]
  );

  const updateAreaFilters = React.useCallback(
    (updates: Partial<ExternalRunDetailSearchParams>) => {
      navigate({
        to: '/runs/external/$runId',
        params: { runId },
        search: (previous: ExternalRunDetailSearchParams) => {
          const merged: ExternalRunDetailSearchParams = { ...previous, ...updates };
          if (!merged.areaQ) delete merged.areaQ;
          if (!merged.areaStates || merged.areaStates.length === 0) delete merged.areaStates;
          return merged;
        },
      });
    },
    [navigate, runId]
  );

  // "Area" is the product name for one shard of a run. A run that predates
  // shard planning, or one that only ever had a single shard, gets exactly
  // ONE synthetic area ("All tests") from the server — so this only turns on
  // the grouped/areas UI for a run that genuinely fanned out. Disabled areas
  // were never launched and don't count toward "fanned out" — a run with one
  // launched area and two disabled ones reads as a single-area run, not a
  // 3-area run. Computed ahead of the loading/error early returns (using
  // `run?.areas ?? []`, mirroring `nonDisabledAreas` above) so the top
  // breadcrumbs can reflect a selected area on first paint.
  const areas = run?.areas ?? [];
  const hasMultipleAreas = nonDisabledAreas.length > 1;
  const hasDisabledAreas = areas.length !== nonDisabledAreas.length;
  // The areas grid is the ONLY place a disabled row renders anywhere on this
  // page (the flat/single-area view only ever reads test rows out of
  // `run.summary.items`, which a disabled area never has). So the grid stays
  // reachable whenever there's a disabled row to show, even if there's only
  // one launched area to group — otherwise a disabled area would be invisible.
  const showAreasGrid = hasMultipleAreas || hasDisabledAreas;
  // A single-area run (no disabled siblings) has nothing to group and no
  // toggle — it always reads as the flat table. A run that fanned out
  // defaults to grouped (the areas grid) unless the URL says otherwise; a
  // run with disabled siblings but only one launched area still defaults to
  // flat (nothing to group there) but honors an explicit `?view=grouped` so
  // the disabled rows stay reachable.
  const view: ResultsView = !showAreasGrid
    ? 'flat'
    : search.view === 'flat'
      ? 'flat'
      : search.view === 'grouped'
        ? 'grouped'
        : hasMultipleAreas
          ? 'grouped'
          : 'flat';
  const selectedArea =
    view === 'grouped' && search.area != null
      ? (areas.find((area) => area.index === search.area) ?? null)
      : null;

  useBreadcrumbs([
    { label: 'Runs', href: '/runs/test' },
    selectedArea
      ? { label: run?.runnerName ?? 'External run', onPress: clearAreaSelection }
      : { label: run?.runnerName ?? 'External run' },
    ...(selectedArea ? [{ label: selectedArea.label }] : []),
  ]);

  if (isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }

  if (isError || !run) {
    return (
      <Page>
        <EmptyState
          icon={<Play size={48} />}
          title="External run not found"
          description="This external test run could not be loaded."
        />
      </Page>
    );
  }

  // Scoped to the requested `?area` (or the whole run when none is
  // selected) — feeds ONLY `RunTriageLockup` below, which is meant to
  // describe whatever scope its icons are drawing. Everything else on this
  // page (the header badge, "N tests", the truncated banner, the empty-state
  // gate) must describe the whole run regardless of scope; see `runWide*`
  // further down. `passed` folds in `flaky`, matching `toSummaryStatus`'s
  // `flaky_success` mapping.
  const failed = run.summary.counts.failed;
  const passed = run.summary.counts.passed + run.summary.counts.flaky;
  const skipped = run.summary.counts.skipped;
  const isSealed = run.status === 'sealed';
  const isFinished = isSealed || run.status === 'canceled';

  // No area selected AND the current view is grouped — draw one icon per
  // area instead of per test. `summary.items` is empty for this scope
  // (grouped-unselected) and is deliberately also NOT what the flat table
  // reads from when no area is picked (`queryParams` above fetches the whole
  // run for flat view), so per-test icons aren't an option here either way;
  // area rollups are the only thing this scope has to draw.
  const isAreasView = view === 'grouped' && !selectedArea;
  const lockupItems = isAreasView ? areaSummaryItems : summaryItems;

  // Run-wide verdict counts, independent of `?area` — summed from `areas`,
  // which the server always returns for the WHOLE run regardless of the
  // requested scope (unlike `summary.counts`, which narrows to the selected
  // area). The page header (title badge, "N tests", the truncated-results
  // banner, the empty-state gate) describes the run as a whole even while
  // `RunTriageLockup` below is legitimately scoped to one selected area.
  const runWideCounts = areas.reduce(
    (acc, area) => ({
      total: acc.total + area.total,
      passed: acc.passed + area.passed,
      failed: acc.failed + area.failed,
      flaky: acc.flaky + area.flaky,
      skipped: acc.skipped + area.skipped,
    }),
    { total: 0, passed: 0, failed: 0, flaky: 0, skipped: 0 }
  );
  const runWideTotal = runWideCounts.total;
  const runWidePassed = runWideCounts.passed + runWideCounts.flaky;
  const runWideFailed = runWideCounts.failed;
  const runWideSkipped = runWideCounts.skipped;

  // The header (title, status badge, "N tests") reads the selected area's own
  // counts while drilled in, and the run-wide counts otherwise — same split
  // `RunTriageLockup` already makes for its icon bar above.
  const headerTotal = selectedArea ? selectedArea.total : runWideTotal;
  const headerPassed = selectedArea ? selectedArea.passed + selectedArea.flaky : runWidePassed;
  const headerFailed = selectedArea ? selectedArea.failed : runWideFailed;
  const headerSkipped = selectedArea ? selectedArea.skipped : runWideSkipped;

  // The un-drilled badge counts areas over `nonDisabledAreas` (a disabled
  // area was never launched and isn't part of "N areas"), so the drilled-in
  // "Area X of Y" must agree and count over the same set rather than
  // `areas.length`, which also includes disabled siblings. A disabled area
  // can still be selected via a hand-edited URL (it renders with zero
  // results) — that's the one case with no position in `nonDisabledAreas`,
  // so it falls back to the area's own index/total, the same numbers this
  // page already read before areas could be excluded from the denominator.
  const selectedAreaPosition = selectedArea
    ? nonDisabledAreas.findIndex((area) => area.index === selectedArea.index) + 1
    : 0;
  const selectedAreaOf = selectedArea
    ? selectedAreaPosition > 0
      ? `${selectedAreaPosition} of ${nonDisabledAreas.length}`
      : `${selectedArea.index} of ${areas.length}`
    : null;

  // Everything that isn't a clean pass, scoped the same way the header
  // counts above are: drilled into an area, it's that area's own tally
  // (which already includes flaky); otherwise it's the run-wide tally. Drives
  // whether "Re-run failed" has anything to do — a scope with nothing to
  // re-run hides the button rather than surfacing a 400 from the server.
  const rerunFailedCount = selectedArea
    ? selectedArea.failed + selectedArea.flaky + selectedArea.skipped
    : runWideCounts.failed + runWideCounts.flaky + runWideCounts.skipped;

  const cancel = async () => {
    try {
      const result = await cancelRun.mutateAsync(runId);
      toast.success(describeExternalCancel(result));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel this run');
    }
  };

  const rerun = async () => {
    try {
      const result = await rerunRun.mutateAsync({ runId });
      navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
    } catch (err) {
      // Includes `EXTERNAL_TEST_RUN_IN_PROGRESS` (a run is already in flight for
      // this runner), `EXTERNAL_TEST_RUN_NOT_RERUNNABLE` (an ingest-API run we
      // have no runner for) and `EXTERNAL_TEST_SUITE_ARCHIVED` (this page
      // outlives the suite it came from, so the button outlives what it would
      // run) — each says more than any wording here could.
      toast.error(err instanceof Error ? err.message : 'Failed to re-run this suite');
    }
  };

  const rerunArea = async (areaIndex: number) => {
    try {
      const result = await rerunRun.mutateAsync({ runId, areaIndex });
      navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to re-run this area');
    }
  };

  const rerunFailed = async () => {
    try {
      const result = await rerunRun.mutateAsync({
        runId,
        failedOnly: true,
        ...(selectedArea ? { areaIndex: selectedArea.index } : {}),
      });
      navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
    } catch (err) {
      // Includes `EXTERNAL_TEST_NO_FAILURES_TO_RERUN` — the scope's own count
      // gating this button's visibility should make that unreachable in
      // practice, but the copy still explains itself if a race gets there.
      toast.error(err instanceof Error ? err.message : 'Failed to re-run the failed tests');
    }
  };

  const toggleAreaDisabled = async (area: RunAreaDto, disabled: boolean) => {
    if (!run.suiteId || !area.specPath) return;
    try {
      await setAreaDisabled.mutateAsync({
        suiteId: run.suiteId,
        runId,
        path: area.specPath,
        disabled,
      });
      toast.success(
        disabled
          ? 'Area turned off. Applies from the next run.'
          : 'Area turned on. Applies from the next run.'
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update this area');
    }
  };

  return (
    <Page>
      <PageHeader
        title={selectedArea ? selectedArea.label : (run.runnerName ?? 'External test run')}
        eyebrow={
          selectedArea && (
            <Text size="xs" className="text-muted-foreground">
              {run.runnerName ?? 'External run'}
            </Text>
          )
        }
        actions={
          <Flex align="center" gap="sm">
            <StatusBadge
              status={normalizeRunStatus(
                run.status === 'sealed'
                  ? headerFailed > 0
                    ? 'failed'
                    : 'success'
                  : run.status === 'canceled'
                    ? 'canceled'
                    : 'running'
              )}
              label={headerStatusLabel(run.status, {
                passed: headerPassed,
                failed: headerFailed,
                skipped: headerSkipped,
              })}
            />
            <VisualChangesBadge scope={{ externalTestRunId: runId }} />
            <AgentPromptButton
              promptUrl={
                selectedArea
                  ? `${API_URL}/external-tests/runs/${runId}/agent-prompt?area=${selectedArea.index}`
                  : `${API_URL}/external-tests/runs/${runId}/agent-prompt`
              }
              label="Copy prompt"
              tooltip={
                selectedArea
                  ? 'Copy a prompt that lets a coding agent investigate this area with the Cofactor CLI'
                  : 'Copy a prompt that lets a coding agent investigate this run with the Cofactor CLI'
              }
            />
            {isFinished ? (
              <>
                {/* Only offered when this run came from a runner of ours; an
                    ingest-API run has nothing to drive, and the server says so. */}
                {run.runnerId && selectedArea && (
                  <Button
                    size="sm"
                    variant="secondary"
                    iconLeft={<ArrowClockwise size={14} />}
                    isDisabled={rerunRun.isPending}
                    onPress={() => void rerunArea(selectedArea.index)}
                  >
                    Re-run area
                  </Button>
                )}
                {run.runnerId && rerunFailedCount > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    iconLeft={<ArrowClockwise size={14} />}
                    isDisabled={rerunRun.isPending}
                    onPress={() => void rerunFailed()}
                  >
                    Re-run failed
                  </Button>
                )}
                {run.runnerId && (
                  <Button
                    size="sm"
                    variant="secondary"
                    iconLeft={<ArrowClockwise size={14} />}
                    isDisabled={rerunRun.isPending}
                    onPress={() => void rerun()}
                  >
                    Run again
                  </Button>
                )}
              </>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                iconLeft={<Stop size={14} />}
                isDisabled={cancelRun.isPending}
                onPress={() => void cancel()}
              >
                Cancel
              </Button>
            )}
          </Flex>
        }
      />

      {run.failureMessage && (
        <Banner variant="error" className="mb-4">
          <Text size="sm">{run.failureMessage}</Text>
        </Banner>
      )}

      <Flex gap="sm" align="center" wrap className="mb-4">
        <TagBadge name="External" color="gray" size="sm" hideDot />
        <TagBadge name={run.provider} color="blue" size="sm" hideDot />
        {run.targetUrl && (
          <Text size="xs" className="text-muted-foreground truncate">
            {run.targetUrl}
          </Text>
        )}
        <Text size="xs" className="text-muted-foreground/70">
          {headerTotal} test{headerTotal === 1 ? '' : 's'}
        </Text>
        {selectedArea ? (
          <Text size="xs" className="text-muted-foreground/70">
            Area {selectedAreaOf}
          </Text>
        ) : (
          showAreasGrid && (
            <Text size="xs" className="text-muted-foreground/70">
              {hasDisabledAreas
                ? `${nonDisabledAreas.length} area${nonDisabledAreas.length === 1 ? '' : 's'} · ${
                    areas.length - nonDisabledAreas.length
                  } disabled`
                : `${areas.length} areas`}
            </Text>
          )
        )}
        {run.rerunOf && (
          <Link
            to="/runs/external/$runId"
            params={{ runId: run.rerunOf.runId }}
            className="no-underline"
          >
            {/* An area re-run carries the directory it selected; a whole-suite
                re-run has no selection and is just "Re-run". */}
            <TagBadge
              name={
                run.selection.specPath && areas.length === 1
                  ? `Re-run of area ${areas[0]!.label}`
                  : 'Re-run'
              }
              color="gray"
              size="sm"
              hideDot
            />
          </Link>
        )}
      </Flex>

      {/*
        Truncated AND empty is a different event from truncated-with-results, and
        conflating them is what this page used to do: it stacked "these results
        were recovered from the tests that had already reported" directly above
        "this run has not reported any test results". Both were true and together
        they explained nothing. Production run 1fd8cd04 rendered exactly that.
      */}
      {run.resultsTruncated && runWideTotal > 0 && (
        <Banner variant="warning" className="mb-4">
          <Text size="sm">
            <strong>Partial results.</strong> This run was cut short before the suite finished, and
            these results were recovered from the tests that had already reported. Tests that never
            started are missing entirely, so the counts below do not describe the whole suite.
          </Text>
        </Banner>
      )}

      {run.artifactCapture?.status === 'partial' && (
        <Banner variant="warning" className="mb-4">
          <Text size="sm">
            <strong>Some run artifacts could not be retained.</strong>{' '}
            {run.artifactCapture.storedCount} of {run.artifactCapture.discoveredCount} discovered
            files were retained for diagnostics. Test results are unaffected.
          </Text>
        </Banner>
      )}

      {run.recordings?.status === 'partial' && (
        <Banner variant="warning" className="mb-4">
          <Text size="sm">
            <strong>Some test recordings could not be prepared.</strong> {run.recordings.readyCount}{' '}
            of {run.recordings.expectedCount} per-test videos are ready. Test results are
            unaffected.
          </Text>
        </Banner>
      )}

      <RunTriageLockup
        // Remounts on scope change so `StatusIconBar`'s internal "Show N
        // more" expansion resets — without this, expanding it on the (huge)
        // areas-overview icon bar left it expanded after drilling into a
        // small area, and vice versa.
        key={isAreasView ? 'areas' : selectedArea ? `area-${selectedArea.index}` : 'all'}
        counts={{
          passed,
          failed,
          skipped,
          completed: isAreasView ? completedAreaCount : passed + failed + skipped,
          total: isAreasView ? nonDisabledAreas.length : run.summary.counts.total,
        }}
        items={lockupItems}
        triageSubject={{ kind: 'externalRun', id: run.id }}
        itemNoun="test"
        progressNoun={isAreasView ? 'areas' : undefined}
        isRunInProgress={!isFinished}
        finishedAt={run.finishedAt}
        onItemSelect={(item) => (isAreasView ? selectArea(Number(item.id)) : openTest(item.id))}
        footer={
          <ExternalRunProgress
            stream={progressState}
            progress={progressState.progress}
            revision={run.revision}
            targetUrl={run.targetUrl}
            isRunFinished={isFinished}
          />
        }
      />

      {showAreasGrid && (
        <Flex justify="end" className="mb-2">
          <PillToggle
            aria-label="Results view"
            value={view}
            onChange={changeView}
            options={RESULTS_VIEW_OPTIONS}
          />
        </Flex>
      )}

      {runWideTotal ? (
        view === 'grouped' && !selectedArea ? (
          // One wrapper, like `ExternalTestsTable`'s: the page's spacing would
          // otherwise open a gap between the toolbar and the table it fuses to.
          <div>
            <ExternalAreasFilterBar
              filters={search}
              onFilterChange={updateAreaFilters}
              stateOptions={areaStateOptions}
            />
            <ExternalRunAreas
              areas={filteredAreas}
              reruns={run.reruns}
              liveShards={progressState.shards}
              isRunFinished={isFinished}
              isRerunPending={rerunRun.isPending}
              onSelectArea={selectArea}
              onRerunArea={(areaIndex) => void rerunArea(areaIndex)}
              runnerDisabledAreas={run.runnerDisabledAreas}
              isSetAreaDisabledPending={setAreaDisabled.isPending}
              onToggleAreaDisabled={(area, disabled) => void toggleAreaDisabled(area, disabled)}
              suiteId={run.suiteId}
            />
          </div>
        ) : (
          <>
            <ExternalTestsTable
              runId={runId}
              scenarios={run.results}
              counts={run.summary.counts}
              page={run.pagination.page}
              pageSize={run.pagination.pageSize}
              pageCount={run.pagination.totalPages}
              totalItems={run.pagination.totalItems}
              isLoading={isFetching}
              filters={search}
              onPageChange={changePage}
              onPageSizeChange={changePageSize}
              onFilterChange={updateFilters}
            />
          </>
        )
      ) : (
        <EmptyState
          icon={<Play size={48} />}
          {...emptyResultsState({ isFinished, resultsTruncated: run.resultsTruncated })}
        />
      )}
    </Page>
  );
}
